import { API_PATCH, API_POST, DBRequest, useConfig } from '../../api/api';
import ExoStepper, { FinalPage } from '../exo/ExoStepper';
import StepRecipient from '../steps/StepRecipient';
import StepDelivery from '../steps/StepDelivery';
import StepCustomStartUp from '../steps/StepCustomStartUp';
import findDifferences from '../exo/findDifferencesInDataCollection';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { StepTemplate } from '../steps/StepTemplate';
import { PositionImage } from '../exo/positions/collapse-modules/PositionImage';
import { PositionDescription } from '../exo/positions/collapse-modules/PositionDescription';
import { PositionNumber } from '../exo/positions/collapse-modules/PositionNumber';
import { PositionText } from '../exo/positions/collapse-modules/PositionText';
import { PositionCheckbox } from '../exo/positions/collapse-modules/PositionCheckbox';
import { PositionProgressPaymentCalculation } from '../exo/positions/collapse-modules/PositionProgressPaymentCalcilation';
import { PositionPreCalculation } from '../exo/positions/collapse-modules/PositionPreCalculation';
import { PositionCopy } from '../exo/positions/collapse-modules/PositionCopy';
import StepPositionsOrder from '../steps/StepPositionsOrder';
import useFetch from '../../api/useFetch';
import SelectPositions from '../select/SelectPositions';
import StepOrder from '../steps/StepOrder';
import StepOrderInvoives from '../steps/StepOrderInvoices';
import StepUser from '../steps/StepUser';
import {
  hasInlineAttachments,
  StepInlineAttachments,
} from '../steps/StepInlineAttachments';
import StepTemplateMetaFields from '../steps/StepTemplateMetaFields';
import { Alert, Box, TextField, Typography, Tooltip } from '@mui/material';
import { numberToString, stringToNumber } from '../special/numberConverter';
import { round } from '../special/round';
import { buildOrderData, excludeInvoiceFromOrder } from '../special/buildOrderData';

// X-Invoice codes for identifing the type of invoice
export const finalInvoiceCode = [877];
export const progressBillCodes = [875, 876, 877, 326, 877];

// Whether the order-summary positions (order total, previous-invoice
// deductions, ...) get auto-generated. Final-invoice templates (typeCode 877)
// generate them by default; a template can opt out via
// metaData.generateFinalPositions: false — the form then behaves like any
// other partial invoice (positions are picked manually in the position steps).
export const shouldGenerateFinalPositions = (templateMetaData) =>
  finalInvoiceCode.includes(templateMetaData?.typeCode) &&
  templateMetaData?.generateFinalPositions !== false;

const NewProgressBilling = ({
  invoices,
  setInvoices,
  onClose,
  startData,
  onUpdate,
  debug,
}) => {
  const { t } = useTranslation();
  const config = useConfig();
  const [steps, setSteps] = useState(generateSteps());

  useEffect(() => {
    if (!startData) return;

    let projId;

    if (
      startData.positions &&
      startData.positions.length > 0 &&
      startData.positions[0].project &&
      startData.positions[0].project.length > 0
    ) {
      projId = startData.positions[0].project[0].id;
      let onlyProject = true;

      startData.positions?.forEach((pos) => {
        if (pos.project?.length != 1 && pos.project?.id != projId) {
          onlyProject = false;
        }
      });

      if (onlyProject) {
        startData.project = startData.positions[0].project[0];
      }
    }
  }, [startData]);

  function getPaymentOptions() {
    let options = [];
    options = config ? config?.invoice.paymentOptions.options : [];
    let selection = config ? config?.invoice.paymentOptions.value : [];

    const filteredOptions = options.filter((option) =>
      selection.includes(option.value),
    );
    return filteredOptions;
  }

  useEffect(() => {
    setSteps(generateSteps());
  }, [startData, config]);

  function generateSteps() {
    var requiredFields = [
      {
        label: t('Title'),
        type: 'text',
        key: 'title',
      },
      {
        label: t("Recipient's Addition"),
        type: 'text',
        key: 'recipientAddition',
      },
      {
        label: t('Invoice Date'),
        type: 'date',
        key: 'date',
      },
      {
        label: t('Delivery Date'),
        type: 'date',
        key: 'deliveryDate',
      },
      {
        label: t('Payment Deadline in Days'),
        type: 'number',
        key: 'paymentDeadlineInDays',
        decimalPlaces: 0,
      },
      {
        label: t('Payment Method'),
        type: 'customSelect',
        key: 'paymentMethod',
        noUnselect: true,
        options: getPaymentOptions(),
      },
      {
        label: `${t('Introduction')} (${t('optional')})`,
        type: 'description',
        key: 'introduction',
        multiline: true,
        textBlockType: 'invoice',
      },
      {
        label: `${t('Document note')} (${t('optional')})`,
        type: 'description',
        key: 'note',
        multiline: true,
        textBlockType: 'invoice',
      },
      {
        label: `${t('Internal Note')} (${t('optional')})`,
        type: 'description',
        key: 'internalNote',
        multiline: true,
        textBlockType: 'invoice',
      },
    ];
    var requiredValidationSchema = Yup.object().shape({
      title: Yup.string().required(t('Title is required')),
      date: Yup.string().required(t('Date is required')),
      deliveryDate: Yup.string().required(t('Date is required')),
      paymentDeadlineInDays: Yup.number().required(
        t('Payment Deadline in Days is required'),
      ),
      paymentMethod: Yup.number().required(),
    });
    if (config) {
      const useDeliveryDateRange = startData
        ? Boolean(startData.deliveryDateStart)
        : Boolean(parseInt(config.invoice.useDeliveryDateRange));

      requiredFields = [
        {
          label: t('Title'),
          type: 'text',
          key: 'title',
        },
        {
          label: t("Recipient's Addition"),
          type: 'text',
          key: 'recipientAddition',
        },
        {
          label: `${t('Project')} (${t('optional')})`,
          type: 'project', //Change to "projectSearch" when TypeProjectSearch is done and we want to make the project searchable
          key: 'project',
          apiUrl: 'projects',
        },
        {
          label: t('Invoice Date'),
          type: 'date',
          key: 'date',
        },
        {
          label: useDeliveryDateRange
            ? t('Delivery Date Range')
            : t('Delivery Date'),
          type: useDeliveryDateRange ? 'dateRange' : 'date',
          key: useDeliveryDateRange ? 'deliveryDate' : 'deliveryDateEnd',
        },
        {
          label: t('Payment Deadline in Days'),
          type: 'number',
          key: 'paymentDeadlineInDays',
          decimalPlaces: 0,
        },
        {
          label: t('Payment Method'),
          type: 'customSelect',
          key: 'paymentMethod',
          noUnselect: true,
          options: getPaymentOptions(),
        },
        {
          label: `${t('Leitweg ID')} (${t('optional')})`,
          type: 'text',
          key: 'leitwegId',
        },
        {
          label: `${t('Introduction')} (${t('optional')})`,
          type: 'description',
          key: 'introduction',
          multiline: true,
          textBlockType: 'invoice',
        },
        {
          label: `${t('Document note')} (${t('optional')})`,
          type: 'description',
          key: 'note',
          multiline: true,
          textBlockType: 'invoice',
        },
        {
          label: `${t('Internal Note')} (${t('optional')})`,
          type: 'description',
          key: 'internalNote',
          multiline: true,
          textBlockType: 'invoice',
        },
      ];
      requiredValidationSchema = useDeliveryDateRange
        ? Yup.object().shape({
            title: Yup.string().required(t('Title is required')),
            date: Yup.string().required(t('Date is required')),
            deliveryDateStart: Yup.string().required(t('Date is required')),
            deliveryDateEnd: Yup.string().required(t('Date is required')),
            paymentDeadlineInDays: Yup.number().required(
              t('Payment Deadline in Days is required'),
            ),
            paymentMethod: Yup.number().required(),
            leitwegId: Yup.string()
              .nullable()
              .max(46, t('Leitweg ID must be at most 46 characters')),
          })
        : Yup.object().shape({
            title: Yup.string().required(t('Title is required')),
            date: Yup.string().required(t('Date is required')),
            deliveryDateEnd: Yup.string().required(t('Date is required')),
            paymentDeadlineInDays: Yup.number().required(
              t('Payment Deadline in Days is required'),
            ),
            paymentMethod: Yup.number().required(),
            leitwegId: Yup.string()
              .nullable()
              .max(46, t('Leitweg ID must be at most 46 characters')),
          });
    }

    const optionalFields = [
      {
        label: `${t('Discount')} (${t('optional')})`,
        type: 'checkbox',
        key: 'discount',
      },
      {
        label: `${t('Discount Rate')} (${t('optional')})`,
        type: 'number',
        key: 'discountRate',
        decimalPlaces: 2,
      },
      {
        label: `${t('Discount Days To Pay')} (${t('optional')})`,
        type: 'number',
        key: 'discountDaysToPay',
        decimalPlaces: 0,
      },
      {
        label: `${t('Images')} (${t('optional')})`,
        type: 'images',
        key: 'additionalFiles',
        maxItems: '15',
        maxSizeKB: '3000',
      },
    ];

    var collapsePositionModules = [
      { component: PositionImage, key: 'image', label: 'Image' },
      {
        component: PositionDescription,
        key: 'description',
        label: 'Description',
        textBlockType: 'invoice',
      },
      {
        component: PositionNumber,
        key: 'taxRate',
        label: 'Tax Rate',
        unit: '%',
        decimalPlaces: 0,
      },
      {
        component: PositionText,
        key: 'unit',
        label: 'Unit',
      },
      {
        component: PositionCopy,
        label: 'Duplicate position',
      },
    ];

    if (startData && startData.sourceType === 'order')
      collapsePositionModules = [
        ...collapsePositionModules,
        {
          component: PositionCheckbox,
          key: 'isOrderPosition',
          label: 'Settle from order',
        },
        {
          component: PositionProgressPaymentCalculation,
          key: 'price',
          label: 'Progress Payment Calculation',
        },
      ];

    collapsePositionModules = [
      ...collapsePositionModules,
      {
        component: PositionPreCalculation,
        key: 'price',
        label: 'Calculation of sales price',
      },
    ];

    return [
      {
        label: t('Order'),
        valid: true,
        content: ({
          updateDataCollection,
          activeStep,
          setActiveStep,
          step,
          dataCollection,
          updateValidation,
        }) => (
          <StepOrder
            onClose={onClose}
            dataCollection={dataCollection}
            updateDataCollection={updateDataCollection}
            activeStep={activeStep}
            setActiveStep={setActiveStep}
            step={step}
            updateValidation={updateValidation}
            noTemplate
            removeDrafts={true}
            includeProject={!(startData && startData.id)}
          />
        ),
      },
      {
        label: t('Recipient'),
        isHiddenFunction: (data) => Boolean(data?.order?.id),
        content: ({
          dataCollection,
          updateValidation,
          updateDataCollection,
        }) => (
          <StepRecipient
            updateDataCollection={updateDataCollection}
            dataCollection={dataCollection}
            updateValidation={updateValidation}
          />
        ),
      },
      {
        label: t('Template'),
        content: ({
          dataCollection,
          updateValidation,
          updateDataCollection,
          setSteps,
          activeStep,
        }) => (
          <StepTemplate
            updateDataCollection={updateDataCollection}
            dataCollection={dataCollection}
            updateValidation={updateValidation}
            templateType="partial_invoice"
            activeStep={activeStep}
            setSteps={setSteps}
          />
        ),
      },
      {
        label: t('Invoices'),
        valid: false,
        content: ({
          updateDataCollection,
          activeStep,
          setActiveStep,
          step,
          dataCollection,
          updateValidation,
        }) => (
          <StepOrderInvoives
            onClose={onClose}
            dataCollection={dataCollection}
            updateDataCollection={updateDataCollection}
            activeStep={activeStep}
            setActiveStep={setActiveStep}
            step={step}
            updateValidation={updateValidation}
          />
        ),
      },

      {
        label: t('Info'),
        content: ({
          dataCollection,
          updateValidation,
          updateDataCollection,
        }) => (
          <StepCustomStartUp
            fields={requiredFields}
            updateDataCollection={updateDataCollection}
            dataCollection={dataCollection}
            updateValidation={updateValidation}
            validationSchema={requiredValidationSchema}
          />
        ),
      },
      {
        label: t('Additional'),
        content: ({
          dataCollection,
          updateValidation,
          updateDataCollection,
        }) => (
          <StepTemplateMetaFields
            updateDataCollection={updateDataCollection}
            dataCollection={dataCollection}
            updateValidation={updateValidation}
            templateMetaData={dataCollection.templateMetaData}
          />
        ),
        isHiddenFunction: (data) =>
          !Boolean(data.templateMetaData?.metaFields?.length > 0),
      },
      {
        label: `(${t('Optional')})`,
        content: ({
          dataCollection,
          updateValidation,
          updateDataCollection,
        }) => (
          <StepCustomStartUp
            fields={optionalFields}
            updateDataCollection={updateDataCollection}
            dataCollection={dataCollection}
            updateValidation={updateValidation}
          />
        ),
      },
      {
        label: t('Order Positions'),
        content: ({
          dataCollection,
          updateValidation,
          updateDataCollection,
        }) => (
          <StepSelectOrderPositions
            updateDataCollection={updateDataCollection}
            dataCollection={dataCollection}
            updateValidation={updateValidation}
          />
        ),
        isHiddenFunction: (data) =>
          shouldGenerateFinalPositions(data?.templateMetaData) ||
          !data?.order ||
          data?.isFlat,
      },
      {
        label: t('Custom Positions'),
        content: ({
          dataCollection,
          updateValidation,
          updateDataCollection,
        }) => (
          <StepPositionsOrder
            updateDataCollection={updateDataCollection}
            dataCollection={dataCollection}
            updateValidation={updateValidation}
            hideTimeBilling={false}
            hideHOAI={false}
            collapseModules={[
              { component: PositionImage, key: 'image', label: 'Image' },
              {
                component: PositionDescription,
                key: 'description',
                label: 'Description',
                textBlockType: 'invoice',
              },
              {
                component: PositionNumber,
                key: 'taxRate',
                label: 'Tax Rate',
                unit: '%',
                decimalPlaces: 0,
              },
              {
                component: PositionText,
                key: 'unit',
                label: 'Unit',
              },
              {
                component: PositionCopy,
                label: 'Duplicate position',
              },
              {
                component: PositionCheckbox,
                key: 'isOrderPosition',
                label: 'Settle from order',
              },
              {
                component: PositionProgressPaymentCalculation,
                key: 'price',
                label: 'Progress Payment Calculation',
              },
            ]}
            imageType={`INVOICE_ATTACHMENT`}
            imageApiFilter={
              startData && startData.id
                ? `orderImages[has]=${startData.id}`
                : `orderImages[doesnothave]=0`
            }
          />
        ),
      },
      {
        label: t('Attachments'),
        content: ({
          dataCollection,
          updateValidation,
          updateDataCollection,
        }) => (
          <StepInlineAttachments
            dataCollection={dataCollection}
            updateDataCollection={updateDataCollection}
            updateValidation={updateValidation}
          />
        ),
        isHiddenFunction: (data) => !hasInlineAttachments(data?.positions),
      },
      {
        label: t('Contact Person'),
        content: ({
          dataCollection,
          updateValidation,
          updateDataCollection,
        }) => (
          <StepUser
            updateDataCollection={updateDataCollection}
            updateValidation={updateValidation}
            dataCollection={dataCollection}
          />
        ),
      },
      {
        label: t('Delivery'),
        content: ({
          dataCollection,
          updateValidation,
          updateDataCollection,
        }) => (
          <StepDelivery
            useInvoiceMail={true}
            updateDataCollection={updateDataCollection}
            dataCollection={dataCollection}
            updateValidation={updateValidation}
            defaultRecipientMailKey="isInvoiceMail"
            allowMailContent={true}
            textBlockType={'invoice'}
          />
        ),
      },
    ];
  }

  const finalStep = {
    operation: ({
      dataCollection,
      handleResponse,
      setIsLoading,
      handleError,
    }) => {
      // Remove internal selection tracking keys before diffing
      const { _orderPositionSelection, _orderPositionTargets, ...cleanData } =
        dataCollection;
      const diff = findDifferences(startData || {}, cleanData);

      if (!diff.order) delete diff.sourceType;

      DBRequest({
        config,
        path: dataCollection.id ? `invoices/${dataCollection.id}` : `invoices`,
        method: cleanData.id
          ? API_PATCH({ ...diff, recipientType: cleanData.recipientType })
          : API_POST(cleanData),
        onResponse: handleRes,
        onLoading: setIsLoading,
        onError: handleError,
      });

      function handleRes(data) {
        handleResponse(data);
        onUpdate(data);
      }
    }, // your operation with the data
    content: (response) => (
      <FinalPage response={response} data={invoices} setData={setInvoices} />
    ), // component with final operations and the feedback
  };

  const [defaultData, setDefaultData] = useState({});

  useEffect(() => {
    if (!config) return;
    setSteps(generateSteps());
    const today = moment().format();

    const useDeliveryDateRange = Boolean(
      parseInt(config.invoice.useDeliveryDateRange),
    );
    const newDefaultData = {
      date: today,
      deliveryDateStart: useDeliveryDateRange ? today : null,
      deliveryDateEnd: today,
      paymentDeadlineInDays: config.invoice.paymentDeadlineInDays,
      discountRate: config.invoice.discountRate,
      discountDaysToPay: config.invoice.discountPaymentDeadlineInDays,
      useDeliveryDateRange,
      sourceType: 'order',
      paymentMethod: getPaymentOptions()[0].value,
    };
    setDefaultData(newDefaultData);
  }, [config]);

  // When editing, load the linked order so the live preview can show the
  // current billable / already-billed amounts and so positions can be enriched
  // with the order's ordered & billed figures (the invoice payload doesn't
  // carry them). Skipped for new invoices — the order is picked in-flow.
  const editOrderId =
    startData && startData.id && startData.order?.id
      ? startData.order.id
      : null;
  const { apiData: orderApiData } = useFetch(
    editOrderId ? `orders/${editOrderId}` : null,
  );

  const correctedStartData = useMemo(() => {
    if (!startData) return {};

    // Remove this invoice's own contribution from the order's billed figures
    // when it's already counted (non-draft), so the preview shows what previous
    // invoices billed rather than double-counting the current one.
    const order = excludeInvoiceFromOrder(orderApiData?.data, startData);
    // Prefer the freshly fetched order positions; fall back to any that came
    // with the invoice payload.
    const orderPositions = order?.positions || startData.orderPositions;
    const base = {
      ...startData,
      positions: completePositionsData(startData.positions, orderPositions),
    };

    // Once the order is loaded, seed the flattened order fields the preview
    // reads (orderPositions, previousInvoices, orderPrice, ...).
    return order ? { ...base, ...buildOrderData(order, startData) } : base;
  }, [startData, orderApiData]);

  return (
    <ExoStepper
      debug={debug}
      allowDraft
      confirmInvoice
      draftType={'progress-invoice'}
      label={
        startData && startData.id
          ? t('Edit Order Based Invoice')
          : t('New Order Based Invoice')
      }
      startDataCollection={
        startData && startData.id
          ? correctedStartData
          : { ...defaultData, ...correctedStartData }
      }
      message={t('Create Invoice')}
      onClose={onClose}
      data={steps}
      finalStep={finalStep}
      previewDocument="partial_invoice"
      syncHoai={false}
    />
  );
};

function hasMoreThanTwoDecimals(num) {
  if (!num) return false;
  return Math.abs(round(num, 2) - num) > 1e-9;
}

const TargetInput = ({ value, decimals = 2, onCommit }) => {
  const [displayValue, setDisplayValue] = useState(
    numberToString(value, decimals),
  );

  useEffect(() => {
    setDisplayValue(numberToString(value, decimals));
  }, [value, decimals]);

  return (
    <TextField
      size="small"
      value={displayValue}
      onChange={(e) => setDisplayValue(e.target.value)}
      onBlur={() => onCommit(stringToNumber(displayValue, decimals))}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.target.blur();
        }
      }}
      inputProps={{
        style: {
          textAlign: 'right',
          width: 70,
          padding: '4px 8px',
        },
      }}
    />
  );
};

const StepSelectOrderPositions = ({
  dataCollection,
  updateValidation,
  updateDataCollection,
}) => {
  const { t } = useTranslation();
  // Prefer the order positions already loaded into the collection: on edit they
  // have THIS invoice's own billing removed (excludeInvoiceFromOrder), so the
  // open amounts are computed against the *previous* invoices only. Falling back
  // to a raw fetch (e.g. if the collection wasn't seeded) still works.
  const { apiData } = useFetch(
    dataCollection.orderPositions ? null : `orders/${dataCollection?.order?.id}`,
  );
  const orderPositions =
    dataCollection.orderPositions || apiData?.data?.positions || null;
  const isLoading = !orderPositions;

  const orderPositionsIds = dataCollection.positions?.map((pos) => pos.id);
  const [convertedPositions, setConvertedPositions] = useState([]);
  const [targets, setTargets] = useState(
    dataCollection._orderPositionTargets || {},
  );
  const [selectedRawPositions, setSelectedRawPositions] = useState(
    dataCollection._orderPositionSelection || [],
  );

  // Pre-select the order positions that are already part of the invoice, so
  // editing (or returning to this step) shows the current selection highlighted
  // instead of an empty list. Runs once, until an explicit selection exists —
  // after that _orderPositionSelection is the source of truth.
  const [selectionSeeded, setSelectionSeeded] = useState(
    Boolean(dataCollection._orderPositionSelection),
  );
  useEffect(() => {
    if (selectionSeeded || isLoading || !orderPositions) return;

    const invoiceOrderPositions = new Map(
      (dataCollection.positions || [])
        .filter((p) => p.isOrderPosition && p.orderPositionId != null)
        .map((p) => [p.orderPositionId, p]),
    );

    const derived = orderPositions.filter((op) =>
      invoiceOrderPositions.has(op.id),
    );

    if (derived.length > 0) {
      // Seed the billing-status targets to the cumulative billed-to-date
      // (previous invoices + this one) so the inputs reflect the invoice.
      const seededTargets = {};
      derived.forEach((op) => {
        if (op.isGroup || op.type === 'percent') return;
        const invoicePos = invoiceOrderPositions.get(op.id);
        const usePercent =
          op.type === 'hoaiPhase' || op.type === 'percentEditable';
        const orderPrice = Number(op.price) || 0;
        // Base already billed by *previous* invoices. Derived from the order
        // copy's billedTotal (this invoice's own billing is already excluded),
        // not from invoicePos.billedAmount — a converted line stores that as 0.
        const billedFraction = orderPrice
          ? round((Number(op.billedTotal) || 0) / orderPrice, 4)
          : 0;
        // This invoice's share of the order position. Converted lines carry
        // amount=1 with a monetary price, so recover the fraction from the money
        // (amount × price ÷ order price) rather than reading 1 as 100%. For a
        // normal line price === order price, so this just yields amount.
        const thisFraction = orderPrice
          ? ((Number(invoicePos.amount) || 0) *
              (Number(invoicePos.price) || 0)) /
            orderPrice
          : Number(invoicePos.amount) || 0;
        const cumulative = billedFraction + thisFraction;
        seededTargets[op.id] = round(cumulative * (usePercent ? 100 : 1), 2);
      });
      setSelectedRawPositions(derived);
      setTargets(seededTargets);
    }
    setSelectionSeeded(true);
  }, [selectionSeeded, isLoading, orderPositions, dataCollection.positions]);

  function processPositions(positions, currentTargets) {
    var newPositions = [];
    const converted = [];

    positions.forEach((element) => {
      const base = {
        ...element,
        isOptional: false,
        orderPositionId: element.id,
        isOrderPosition: true,
        totalAmount: element.amount,
        orderedAmount: element.amount,
        orderedPrice: element.price,
        billedAmount: element.billedAmount || 0,
      };

      const roundPrecision = element.type === 'default' ? 2 : 4;

      // `percent` summary lines and groups derive their amount/price from the
      // sibling subtotal recalculation below — bill them as-is here.
      if (element.type === 'percent' || element.isGroup) {
        newPositions.push({
          ...base,
          amount: round(element.amount, roundPrecision),
          type: element.type,
          price: element.price,
        });
        return;
      }

      const usePercent =
        element.type === 'hoaiPhase' || element.type === 'percentEditable';
      const price = Number(element.price) || 0;
      // billedTotal is the exact money billed so far; it is the source of truth.
      // We deliberately do NOT go through `billedAmount` (a quantity rounded to
      // 4 decimals): for large prices a 4-decimal error in the fraction is euros
      // of error, which is what previously forced spurious conversions.
      const billedMoney = Number(element.billedTotal) || 0;

      // Cumulative money that should be billed for this position after this
      // invoice: the target share of the ordered total, or — with no explicit
      // target — the full ordered total (bill everything still open).
      const targetRaw =
        currentTargets[element.id] != null ? currentTargets[element.id] : null;
      const targetFraction =
        targetRaw != null
          ? usePercent
            ? targetRaw / 100
            : targetRaw
          : Number(element.amount) || 0;
      const cumulativeMoney = round(targetFraction * price, 2);

      // Money left to bill now. Earlier invoices may have over-billed by a
      // rounding cent, so clamp at zero — a fully billed position has nothing
      // left to bill and must not be converted or produce a negative line.
      let openMoney = round(cumulativeMoney - billedMoney, 2);
      if (openMoney == null || openMoney <= 0) openMoney = 0;

      let posType = element.type;
      let posPrice = price;
      let openAmount = 0;
      let didConvert = false;

      if (openMoney > 0 && price > 0) {
        const roundedQty = round(openMoney / price, roundPrecision);
        // Keep the percentage/quantity representation only when it multiplies
        // back to the exact open money — compared in whole cents to sidestep
        // floating-point error (e.g. 0.125 stored as 0.1249999). A small
        // percentage of a large price often can't be expressed at 4 decimals,
        // so fall back to a fixed-amount line billing the exact money.
        const qtyCents = Math.round(round(roundedQty * price, 2) * 100);
        const openCents = Math.round(openMoney * 100);
        if (roundedQty !== 0 && qtyCents === openCents) {
          openAmount = roundedQty;
        } else {
          openAmount = 1;
          posType = 'default';
          posPrice = openMoney;
          didConvert = true;
          converted.push(element.name || element.position);
        }
      }

      newPositions.push({
        ...base,
        // Billed quantity for the "billable units to date" display
        // (billedAmount + amount). Derived from the exact billed money so it's
        // present even for seeded positions. A converted fixed line has no
        // meaningful billed quantity, so keep whatever the position carried.
        billedAmount:
          didConvert || price === 0
            ? element.billedAmount || 0
            : round(billedMoney / price, 4),
        amount: round(openAmount, roundPrecision),
        type: posType,
        price: round(posPrice, 2),
      });
    });

    setConvertedPositions(converted);
    const sorted = sortByPosition(newPositions);

    // Recalculate percent position prices based on billing amounts of siblings
    // (same algorithm as ExoPositions.jsx but using the billing totals)
    const maxDepth =
      sorted.reduce((max, p) => Math.max(max, p.depth || 0), 0) + 1;
    let subtotalsNetto = Array(maxDepth).fill(0);
    let percentPosToUpdate = [];
    let outerSumNetto = 0;

    for (let i = sorted.length - 1; i >= 0; i--) {
      const pos = sorted[i];

      if (pos.type === 'percent') {
        percentPosToUpdate.push(i);
      } else if (pos.isGroup) {
        let additionalPercentNetto = 0;
        for (let k = percentPosToUpdate.length - 1; k >= 0; k--) {
          if (
            (sorted[percentPosToUpdate[k]].depth || 0) - 1 ===
            (pos.depth || 0)
          ) {
            sorted[percentPosToUpdate[k]].price = round(
              subtotalsNetto[pos.depth || 0],
              2,
            );
            additionalPercentNetto += round(
              subtotalsNetto[pos.depth || 0] *
                sorted[percentPosToUpdate[k]].amount,
              2,
            );
            percentPosToUpdate.splice(k, 1);
          }
        }
        // Add percent contributions to parent depths
        for (let j = 0; j < (pos.depth || 0); j++) {
          subtotalsNetto[j] += additionalPercentNetto;
        }
        outerSumNetto += additionalPercentNetto;
        subtotalsNetto[pos.depth || 0] = 0;
      } else if (!pos.isOptional) {
        const posTotal = round(Number(pos.amount) * Number(pos.price), 2);
        for (let j = 0; j < (pos.depth || 0); j++) {
          subtotalsNetto[j] += posTotal;
        }
        outerSumNetto += posTotal;
      }
    }

    // Orphan percent positions (no parent group) get the outer sum
    for (let k = percentPosToUpdate.length - 1; k >= 0; k--) {
      sorted[percentPosToUpdate[k]].price = round(outerSumNetto, 2);
    }

    return sorted;
  }

  function handleSelectedPositions(positions) {
    setSelectedRawPositions(positions);

    // Initialize targets for newly selected positions, clean up deselected
    const newTargets = { ...targets };
    const selectedIds = positions.map((p) => p.id);

    positions.forEach((element) => {
      if (
        newTargets[element.id] == null &&
        !element.isGroup &&
        element.type !== 'percent'
      ) {
        const usePercent =
          element.type === 'hoaiPhase' || element.type === 'percentEditable';
        newTargets[element.id] = round(
          element.amount * (usePercent ? 100 : 1),
          2,
        );
      }
    });

    // Remove targets for deselected positions
    Object.keys(newTargets).forEach((id) => {
      if (!selectedIds.includes(Number(id))) {
        delete newTargets[id];
      }
    });

    setTargets(newTargets);

    // Check if selection actually changed
    const prevIds = new Set(
      (dataCollection._orderPositionSelection || []).map((p) => p.id),
    );
    const newIds = new Set(selectedIds);
    const selectionChanged =
      prevIds.size !== newIds.size ||
      [...newIds].some((id) => !prevIds.has(id));

    if (!selectionChanged) {
      updateDataCollection({
        _orderPositionSelection: positions,
        _orderPositionTargets: newTargets,
      });
      return;
    }

    const orderPositions = processPositions(positions, newTargets);
    const customPositions = getValidCustomPositions(orderPositions);
    updateDataCollection({
      positions: [...orderPositions, ...customPositions],
      _orderPositionSelection: positions,
      _orderPositionTargets: newTargets,
    });
  }

  function getValidCustomPositions(orderPositions) {
    // Importing order positions replaces every line tied to a specific order
    // position (orderPositionId set) and rebuilds isGenerated summary lines.
    // Match on orderPositionId — not the isOrderPosition flag — so a
    // converted/reloaded order line can't survive as "custom" and end up
    // duplicated on the document. Everything else is preserved, including
    // "settle from order" lines: they are billed against the order (isOrderPosition
    // true, orderId set) but have no orderPositionId, so they must survive.
    const allCustom = (dataCollection.positions || []).filter(
      (p) => p.orderPositionId == null && !p.isGenerated,
    );
    // Depths that still have a group after the import.
    const groupDepths = new Set(
      orderPositions.filter((p) => p.isGroup).map((p) => p.depth),
    );
    // Keep every custom line. If its parent group no longer exists after the
    // import, lift it to the top level instead of dropping it (dropping left the
    // line out of the form yet lingering on the document).
    return allCustom.map((p) =>
      p.depth === 0 || p.depth == null || groupDepths.has(p.depth - 1)
        ? p
        : { ...p, depth: 0 },
    );
  }

  function handleTargetCommit(positionId, value, element) {
    const usePercent =
      element.type === 'hoaiPhase' || element.type === 'percentEditable';
    const billedDisplay = (element.billedAmount || 0) * (usePercent ? 100 : 1);
    const totalDisplay = element.amount * (usePercent ? 100 : 1);

    const parsed = parseFloat(value);
    if (isNaN(parsed)) return;

    const lower = Math.min(billedDisplay, totalDisplay);
    const upper = Math.max(billedDisplay, totalDisplay);
    const clamped = round(Math.max(lower, Math.min(upper, parsed)), 4);

    const newTargets = { ...targets, [positionId]: clamped };
    setTargets(newTargets);
    const orderPositions = processPositions(selectedRawPositions, newTargets);
    const customPositions = getValidCustomPositions(orderPositions);
    updateDataCollection({
      positions: [...orderPositions, ...customPositions],
      _orderPositionTargets: newTargets,
    });
  }

  useEffect(() => {
    updateValidation(true);
  }, []);

  function sortByPosition(arr) {
    return arr.sort((a, b) => {
      const posA = a.position.split('.').map(Number);
      const posB = b.position.split('.').map(Number);

      // Compare each part of the position
      for (let i = 0; i < Math.max(posA.length, posB.length); i++) {
        const partA = posA[i] || 0; // Default to 0 if part is undefined
        const partB = posB[i] || 0; // Default to 0 if part is undefined

        if (partA !== partB) {
          return partA - partB;
        }
      }

      // If all parts are equal
      return 0;
    });
  }

  function renderBillingStatus(element, selected) {
    if (element.isGroup || element.type === 'percent') return null;

    const usePercent =
      element.type === 'hoaiPhase' || element.type === 'percentEditable';
    let rawBilled = element.billedAmount || 0;
    if (!rawBilled && element.billedTotal && element.price) {
      rawBilled = round(element.billedTotal / element.price, 4);
    }
    const billedAmount = round(
      rawBilled * (usePercent ? 100 : 1),
      usePercent ? 2 : 4,
    );
    const totalAmount = round(
      element.amount * (usePercent ? 100 : 1),
      usePercent ? 2 : 4,
    );
    const unit = usePercent ? '%' : element.unit || '';
    const decimals = 2;

    if (!selected) {
      return (
        <Tooltip title={t('Billing Status')}>
          <Box
            className="monospace text-right min-w-fit flex flex-col items-end w-32"
            sx={{ ml: 1 }}
          >
            <Typography fontSize={13}>
              {numberToString(billedAmount, decimals)} {unit}
            </Typography>
            <Typography fontSize={11} color="text.secondary">
              / {numberToString(totalAmount, decimals)} {unit}
            </Typography>
          </Box>
        </Tooltip>
      );
    }

    const currentTarget = round(
      targets[element.id] != null ? targets[element.id] : totalAmount,
      decimals,
    );

    return (
      <Box
        className="flex flex-col items-end"
        sx={{ ml: 1, minWidth: 130 }}
        onClick={(e) => e.stopPropagation()}
      >
        <Box className="flex items-center gap-1">
          <TargetInput
            value={currentTarget}
            decimals={decimals}
            onCommit={(val) => handleTargetCommit(element.id, val, element)}
          />
          <Typography variant="caption" sx={{ whiteSpace: 'nowrap' }}>
            {unit}
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box className="relative">
      <Box className="flex justify-end px-4 mb-2 mt-1 absolute top-0 right-0">
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ width: 130, textAlign: 'right' }}
        >
          {t('Billing Status')}
        </Typography>
      </Box>
      <SelectPositions
        // Remount once the initial selection has been seeded so the pre-checked
        // items take effect (initialCheckedItems is only read on mount).
        key={selectionSeeded ? 'seeded' : 'initial'}
        positions={isLoading ? false : orderPositions}
        isLoading={isLoading}
        onSelect={handleSelectedPositions}
        allowAll={true}
        requirePercent={true}
        renderExtra={renderBillingStatus}
        initialCheckedItems={selectedRawPositions}
      />
      {convertedPositions.length > 0 && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          {t(
            'The following positions were converted to fixed-amount positions due to rounding precision',
          )}
          : {convertedPositions.join(', ')}
        </Alert>
      )}
    </Box>
  );
};

function completePositionsData(positions, orderPositions) {
  if (!positions) return positions;

  return positions.map((pos) => {
    // The "Settle from order" checkbox is bound to isOrderPosition, but the
    // persisted source of truth is orderId — set for every position billed
    // against the order, including manually-added "settle from order" lines that
    // have no orderPositionId. Restore the flag on load so the checkbox reflects
    // the saved state (and the linkage isn't dropped on the next save).
    const normalized = pos.orderId ? { ...pos, isOrderPosition: true } : pos;

    // Only a line tied to a specific order position can be enriched with that
    // position's ordered/billed figures. "Settle from order" lines
    // (orderPositionId null) carry their own amounts and pass through untouched.
    if (!orderPositions || normalized.orderPositionId == null) return normalized;

    // add the ordered amount/price and billed figures from the linked order
    const orderPosition = orderPositions.find(
      (obj) => obj.id === normalized.orderPositionId,
    );
    if (!orderPosition) {
      return {
        ...normalized,
        orderedAmount: 0,
        orderedPrice: 0,
        totalAmount: 0,
        billedAmount: 0,
        billedTotal: 0,
      };
    }

    // Order positions carry billedTotal (money) but not billedAmount
    // (quantity). Derive the billed quantity the same way the position
    // selection does (see SelectPositions), so "billable units to date"
    // (billedAmount + amount) reflects all previous invoices, not just this one.
    let billedAmount = orderPosition.billedAmount;
    if (
      billedAmount == null &&
      normalized.type !== 'percent' &&
      orderPosition.billedTotal &&
      orderPosition.price
    ) {
      billedAmount = round(orderPosition.billedTotal / orderPosition.price, 4);
    }

    return {
      ...normalized,
      orderedAmount: orderPosition.amount,
      orderedPrice: orderPosition.price,
      totalAmount: orderPosition.amount,
      billedAmount: billedAmount || 0,
      billedTotal: orderPosition.billedTotal,
    };
  });
}

export default NewProgressBilling;
